<template>
    <pendo-multiselect
        :options="groupedOptions"
        :value="selectedValue"
        group-options-key="options"
        group-label-key="label"
        :multiple="false"
        :close-on-select="true"
        :max-menu-height="400"
        :placeholder="$t('askPendo.savedChat.Select an option')"
        :no-results-text="$t('askPendo.savedChat.No conversations found')"
        @select="handleSelect">
        <template #trigger="{ comboboxAttrs, handleKeydown, toggleMenu, isOpen, setTriggerRef }">
            <button
                :ref="setTriggerRef"
                v-bind="comboboxAttrs"
                v-pendo-tooltip="triggerTooltip"
                type="button"
                class="saved-chat-trigger"
                :class="{ 'is-open': isOpen, 'saved-chat-trigger--icon-only': iconOnly }"
                @click="toggleMenu"
                @keydown="handleKeydown">
                <template v-if="iconOnly">
                    <pendo-icon
                        type="list"
                        size="16" />
                </template>
                <template v-else>
                    <pendo-icon
                        type="list"
                        size="16" />
                    <span class="saved-chat-trigger__label">
                        {{ $t('askPendo.savedChat.Previous chats') }}
                    </span>
                    <pendo-icon
                        type="chevron-down"
                        size="12"
                        class="saved-chat-trigger__chevron"
                        :class="{ rotated: isOpen }" />
                </template>
            </button>
        </template>
        <template #noData>
            <div class="no-data-message">
                {{ $t('askPendo.savedChat.No previous chats') }}
            </div>
        </template>

        <template #header="{ comboboxAttrs, handleKeydown, updateInputValue, inputValue }">
            <div class="custom-search-header">
                <search
                    v-bind="comboboxAttrs"
                    :value="inputValue"
                    autofocus
                    full-width
                    :placeholder="$t('askPendo.savedChat.Search...')"
                    @input="updateInputValue"
                    @keydown="handleKeydown" />
            </div>
        </template>
    </pendo-multiselect>
</template>

<script>
import { PendoMultiselect, PendoIcon, PendoNotification, PendoTooltip } from '@pendo/components';
import Search from '@/common/search/search.vue';
import { mapActions, mapGetters } from 'vuex';
import { formatConversationsForDropdown } from '@/ask-pendo/utils/conversation-grouping.utils';
import { EventBus } from '@utils/event-bus';

export default {
    name: 'AskPendoSavedChat',
    components: {
        PendoMultiselect,
        PendoIcon,
        Search,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        iconOnly: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            selectedValue: null,
        };
    },
    computed: {
        ...mapGetters('askPendo', ['userConversations']),

        groupedOptions () {
            return formatConversationsForDropdown(this.userConversations);
        },

        triggerTooltip () {
            return this.iconOnly ? this.$t('askPendo.savedChat.Previous chats') : null;
        },
    },
    async mounted () {
        try {
            await this.loadAll();
            EventBus.$on('conversation:saved', this.handleConversationSaved);
        } catch {
            PendoNotification({
                type: 'error',
                title: this.$t('askPendo.savedChat.Unable to load previous chats'),
                message: this.$t('askPendo.savedChat.Please try again later'),
                duration: 4000,
            });
        }
    },
    beforeUnmount () {
        EventBus.$off('conversation:saved', this.handleConversationSaved);
    },
    methods: {
        ...mapActions('askPendo', ['loadAll']),

        handleConversationSaved () {
            this.loadAll({ reFetch: true });
        },

        handleSelect (option) {
            this.selectedValue = option;
            this.$router.push({
                name: 'AgentModeConversation',
                params: { id: option.id },
            });
        },
    },
};
</script>

<style scoped lang="less">
.saved-chat-trigger {
    color: var(--palette-gray-90);
    background-color: var(--palette-gray-0);
    border: 1px solid var(--palette-gray-40);
    border-radius: 4px;
    height: 36px;
    padding: 0 8px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    user-select: none;
    transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;

    &:hover,
    &.is-open {
        background-color: var(--palette-gray-10);
        border-color: var(--palette-gray-60);
        color: var(--palette-gray-90);
    }

    &:focus-visible {
        outline: 1px solid var(--palette-gray-90);
    }

    &__label {
        white-space: nowrap;
    }

    &__chevron {
        transition: transform 0.2s ease;
        margin-left: auto;

        &.rotated {
            transform: rotate(180deg);
        }
    }

    &--icon-only {
        width: 32px;
        min-width: 32px;
        height: 32px;
        padding: 0;
        justify-content: center;
        border-color: transparent;
        background-color: transparent;

        &:hover,
        &.is-open {
            border-color: transparent;
            background-color: var(--palette-gray-10);
        }
    }
}

.custom-search-header {
    padding: 12px 16px;
    border-bottom: 1px solid var(--palette-gray-40);
}
</style>
